import React, { useState, useEffect, useCallback } from 'react';

const PronounMappingsAdmin = ({ API, adminToken, isDarkMode }) => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const dk = isDarkMode;

  const fetchMappings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/grammar/pronoun-mappings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMappings(data.mappings || []);
      }
    } catch (err) {
      console.error('Failed to fetch pronoun mappings:', err);
    } finally {
      setLoading(false);
    }
  }, [API, adminToken]);

  useEffect(() => { fetchMappings(); }, [fetchMappings]);

  const startEdit = (m) => {
    setEditingId(m._id);
    setEditForm({
      latin: m.latin || '',
      arabic: m.arabic || '',
      alt_latin: m.alt_latin || '',
      alt_arabic: m.alt_arabic || '',
      description: m.description || ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/grammar/pronoun-mappings/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(editForm)
      });
      if (res.ok) {
        setEditingId(null);
        setEditForm({});
        fetchMappings();
      }
    } catch (err) {
      console.error('Failed to save pronoun mapping:', err);
    } finally {
      setSaving(false);
    }
  };

  const slotColors = {
    'i': { bg: dk ? 'bg-blue-900/30' : 'bg-blue-50', border: dk ? 'border-blue-700/50' : 'border-blue-200', text: dk ? 'text-blue-300' : 'text-blue-700' },
    'you_sm': { bg: dk ? 'bg-emerald-900/30' : 'bg-emerald-50', border: dk ? 'border-emerald-700/50' : 'border-emerald-200', text: dk ? 'text-emerald-300' : 'text-emerald-700' },
    'you_sf': { bg: dk ? 'bg-pink-900/30' : 'bg-pink-50', border: dk ? 'border-pink-700/50' : 'border-pink-200', text: dk ? 'text-pink-300' : 'text-pink-700' },
    'he': { bg: dk ? 'bg-indigo-900/30' : 'bg-indigo-50', border: dk ? 'border-indigo-700/50' : 'border-indigo-200', text: dk ? 'text-indigo-300' : 'text-indigo-700' },
    'she': { bg: dk ? 'bg-purple-900/30' : 'bg-purple-50', border: dk ? 'border-purple-700/50' : 'border-purple-200', text: dk ? 'text-purple-300' : 'text-purple-700' },
    'it': { bg: dk ? 'bg-gray-800/50' : 'bg-gray-50', border: dk ? 'border-gray-600' : 'border-gray-200', text: dk ? 'text-gray-300' : 'text-gray-600' },
    'we': { bg: dk ? 'bg-amber-900/30' : 'bg-amber-50', border: dk ? 'border-amber-700/50' : 'border-amber-200', text: dk ? 'text-amber-300' : 'text-amber-700' },
    'you_p': { bg: dk ? 'bg-teal-900/30' : 'bg-teal-50', border: dk ? 'border-teal-700/50' : 'border-teal-200', text: dk ? 'text-teal-300' : 'text-teal-700' },
    'they': { bg: dk ? 'bg-orange-900/30' : 'bg-orange-50', border: dk ? 'border-orange-700/50' : 'border-orange-200', text: dk ? 'text-orange-300' : 'text-orange-700' },
  };

  const slotOrder = ['i', 'you_sm', 'you_sf', 'he', 'she', 'it', 'we', 'you_p', 'they'];

  const sorted = [...mappings].sort((a, b) => {
    return slotOrder.indexOf(a.pronoun_slot) - slotOrder.indexOf(b.pronoun_slot);
  });

  if (loading) {
    return (
      <div className={`p-6 rounded-lg border ${dk ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <p className={dk ? 'text-gray-400' : 'text-gray-500'}>Loading pronoun mappings...</p>
      </div>
    );
  }

  return (
    <div className={`p-6 rounded-lg border ${dk ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <h3 className={`text-lg font-bold mb-3 ${dk ? 'text-white' : 'text-gray-800'}`}>
        Pronoun Mappings
      </h3>

      {/* Explanation box */}
      <div className={`mb-6 p-4 rounded-lg border text-sm leading-relaxed ${dk ? 'bg-indigo-900/30 border-indigo-700/50 text-indigo-300' : 'bg-indigo-50 border-indigo-200 text-indigo-700'}`}>
        <strong>What are these?</strong> These are the 9 Darija pronouns used across the verb system.
        Every verb conjugation starts with a pronoun — for example "Anta swalti" (You asked, male)
        or "Hena swalna" (We asked). The audit reads these pronoun words from the latinScript
        to know which slot each entry belongs to.
        <br /><br />
        <strong>How the audit uses them:</strong> When the audit finds 3 entries with English "You",
        it looks at the Darija to tell them apart. Example for the verb "ask":
        <br />
        &bull; <strong>Anta</strong> swalti (S)(M) → detected as <em>you_sm</em> (singular masculine)
        <br />
        &bull; <strong>Anti</strong> swalti (S)(F) → detected as <em>you_sf</em> (singular feminine)
        <br />
        &bull; <strong>Antoma</strong> swalto (P) → detected as <em>you_p</em> (plural)
        <br /><br />
        <strong>Alt Latin:</strong> Alternative spellings the audit also recognizes.
        For example "Ntoma" is an alternative for "Antoma" — both map to you_p.
        Edit these if you use different spellings in your verb entries.
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sorted.map(m => {
          const colors = slotColors[m.pronoun_slot] || slotColors['it'];
          const isEditing = editingId === m._id;

          return (
            <div
              key={m._id}
              className={`p-4 rounded-lg border ${colors.bg} ${colors.border} transition-all`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className={`text-lg font-bold ${colors.text}`}>{m.english}</span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${dk ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                    {m.pronoun_slot}
                  </span>
                </div>
                {!isEditing && (
                  <button
                    onClick={() => startEdit(m)}
                    className={`px-3 py-1 text-xs rounded-lg transition-all ${dk ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-600 hover:bg-gray-100'} border ${dk ? 'border-gray-600' : 'border-gray-300'}`}
                  >
                    Edit
                  </button>
                )}
              </div>

              {isEditing ? (
                /* Edit form */
                <div className="space-y-2">
                  <div>
                    <label className={`text-xs font-medium ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Latin Script</label>
                    <input
                      type="text"
                      value={editForm.latin}
                      onChange={e => setEditForm({...editForm, latin: e.target.value})}
                      className={`w-full px-3 py-1.5 rounded border text-sm ${dk ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                    />
                  </div>
                  <div>
                    <label className={`text-xs font-medium ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Arabic Script</label>
                    <input
                      type="text"
                      dir="rtl"
                      value={editForm.arabic}
                      onChange={e => setEditForm({...editForm, arabic: e.target.value})}
                      className={`w-full px-3 py-1.5 rounded border text-sm ${dk ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                    />
                  </div>
                  <div>
                    <label className={`text-xs font-medium ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Alt Latin (optional)</label>
                    <input
                      type="text"
                      value={editForm.alt_latin}
                      onChange={e => setEditForm({...editForm, alt_latin: e.target.value})}
                      className={`w-full px-3 py-1.5 rounded border text-sm ${dk ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                    />
                  </div>
                  <div>
                    <label className={`text-xs font-medium ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Alt Arabic (optional)</label>
                    <input
                      type="text"
                      dir="rtl"
                      value={editForm.alt_arabic}
                      onChange={e => setEditForm({...editForm, alt_arabic: e.target.value})}
                      className={`w-full px-3 py-1.5 rounded border text-sm ${dk ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                    />
                  </div>
                  <div>
                    <label className={`text-xs font-medium ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Description</label>
                    <input
                      type="text"
                      value={editForm.description}
                      onChange={e => setEditForm({...editForm, description: e.target.value})}
                      className={`w-full px-3 py-1.5 rounded border text-sm ${dk ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => saveEdit(m._id)}
                      disabled={saving}
                      className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-all disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={cancelEdit}
                      className={`px-3 py-1.5 text-xs rounded-lg transition-all ${dk ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* Display view */
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium w-14 ${dk ? 'text-gray-500' : 'text-gray-400'}`}>Latin:</span>
                    <span className={`text-sm font-semibold ${dk ? 'text-white' : 'text-gray-800'}`}>{m.latin}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium w-14 ${dk ? 'text-gray-500' : 'text-gray-400'}`}>Arabic:</span>
                    <span className={`text-sm font-semibold ${dk ? 'text-white' : 'text-gray-800'}`} dir="rtl">{m.arabic}</span>
                  </div>
                  {m.alt_latin && (
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium w-14 ${dk ? 'text-gray-500' : 'text-gray-400'}`}>Alt:</span>
                      <span className={`text-xs ${dk ? 'text-gray-400' : 'text-gray-500'}`}>{m.alt_latin}</span>
                    </div>
                  )}
                  {m.alt_arabic && (
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium w-14 ${dk ? 'text-gray-500' : 'text-gray-400'}`}>Alt Ar:</span>
                      <span className={`text-xs ${dk ? 'text-gray-400' : 'text-gray-500'}`} dir="rtl">{m.alt_arabic}</span>
                    </div>
                  )}
                  {m.description && (
                    <p className={`text-xs mt-1 ${dk ? 'text-gray-500' : 'text-gray-400'}`}>{m.description}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {mappings.length === 0 && (
        <p className={`text-center py-8 ${dk ? 'text-gray-500' : 'text-gray-400'}`}>
          No pronoun mappings found. They will be auto-seeded when the page loads.
        </p>
      )}
    </div>
  );
};

export default PronounMappingsAdmin;
